import { useEffect, useState } from "react";
import { KYCProviders } from "consts/kyc";
import { createPspKycSetting } from "features/pspKycSettings/api";
import { resetPspKycSettings } from "features/pspKycSettings/slice";
import type { PspKycSettingCreatePayload } from "features/pspKycSettings/types";
import { selectPaymentMethodsData } from "features/paymentMethods/selectors";
import usePaymentMethodsActions from "features/paymentMethods/useActions";
import { useAppDispatch } from "hooks/store";
import { useTypedSelector } from "hooks";

type UseKycSettingFormOptions = {
    readonly onSuccess?: () => void;
};

export type PspKycSettingFormValues = Omit<PspKycSettingCreatePayload, 'kycTypeId'> & {
    kycTypeIds: Array<number>;
};

export type FieldErrors = Partial<Record<keyof PspKycSettingFormValues, string>>;

type ResponseEnvelope = {
    success?: boolean;
    code?: number;
    message?: string;
    data?: unknown;
    errors?: Record<string, Array<string>>;
};

const initialValues: PspKycSettingFormValues = {
    MOR: '',
    methodId: 0,
    PSP: '',
    countryIso2: '',
    kycProvider: '',
    kycTypeIds: []
};

const isErrorEnvelope = (json: ResponseEnvelope) =>
    json?.success === false || (typeof json?.code === 'number' && json.code >= 400);

const extractFieldErrors = (json: ResponseEnvelope): FieldErrors => {
    const source = json?.errors
        ?? (json?.data && typeof json.data === 'object' && !Array.isArray(json.data)
            ? json.data as Record<string, Array<string>>
            : null);

    if (!source) return {};

    const mapped: FieldErrors = {};
    Object.entries(source).forEach(([key, messages]) => {
        if (Array.isArray(messages) && messages.length > 0) {
            // Map backend's kycTypeId errors onto our kycTypeIds field
            const formKey = key === 'kycTypeId' ? 'kycTypeIds' : key;
            mapped[formKey as keyof PspKycSettingFormValues] = messages[0];
        }
    });
    return mapped;
};

export default function useKycSettingForm({ onSuccess }: UseKycSettingFormOptions = {}) {
    const dispatch = useAppDispatch();
    const { getAllPaymentMethods } = usePaymentMethodsActions();
    const paymentMethods = useTypedSelector(selectPaymentMethodsData());

    const [formValues, setFormValues] = useState<PspKycSettingFormValues>(initialValues);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        getAllPaymentMethods();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFieldChange = <K extends keyof PspKycSettingFormValues>(
        name: K,
        value: PspKycSettingFormValues[K]
    ) => {
        setFormValues(prev => {
            const next = { ...prev, [name]: value };
            if (name === 'PSP') {
                const newPsp = value as string;
                if (next.methodId > 0) {
                    const currentMethod = paymentMethods.find(
                        m => Number(m.coreId) === next.methodId
                    );
                    if (currentMethod && currentMethod.PSP !== newPsp) {
                        next.methodId = 0;
                    }
                }
            } else if (name === 'methodId') {
                const newMethodId = value as number;
                if (newMethodId > 0) {
                    const newMethod = paymentMethods.find(
                        m => Number(m.coreId) === newMethodId
                    );
                    if (newMethod && newMethod.PSP && newMethod.PSP !== next.PSP) {
                        next.PSP = newMethod.PSP;
                    }
                }
            }

            return next;
        });
        setFieldErrors(prev => {
            const keysToClear: Array<keyof PspKycSettingFormValues> = [name];
            if (name === 'PSP') keysToClear.push('methodId');
            if (name === 'methodId') keysToClear.push('PSP');

            const hasAny = keysToClear.some(k => prev[k]);
            if (!hasAny) return prev;

            const next = { ...prev };
            keysToClear.forEach(k => { delete next[k]; });
            return next;
        });
    };

    const validate = (values: PspKycSettingFormValues): FieldErrors => {
        const errors: FieldErrors = {};

        if (!values.PSP) {
            errors.PSP = 'PSP Provider is required';
        }
        if (!values.kycProvider) {
            errors.kycProvider = 'KYC Provider is required';
        }
        if (values.kycProvider === KYCProviders.Manual) {
            errors.kycProvider = 'Invalid KYC Provider';
        }
        if (!Array.isArray(values.kycTypeIds) || values.kycTypeIds.length === 0) {
            errors.kycTypeIds = 'At least one KYC Type is required';
        }
        if (values.countryIso2 === undefined || values.countryIso2 === null) {
            errors.countryIso2 = 'Country is required';
        }
        if (values.methodId < 0) {
            errors.methodId = 'Invalid Payment Method';
        }

        return errors;
    };

    const validateForm = () => {
        const errors = validate(formValues);
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const resetForm = () => {
        setFormValues(initialValues);
        setFieldErrors({});
        setError(null);
    };

    const handleSubmit = async (): Promise<boolean> => {
        if (!validateForm()) {
            return false;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const { kycTypeIds, ...rest } = formValues;

            const results = await Promise.allSettled(
                kycTypeIds.map(async (typeId) => {
                    const response = await createPspKycSetting({
                        ...rest,
                        kycTypeId: typeId
                    });
                    const json: ResponseEnvelope = await response.json();
                    if (!response.ok || isErrorEnvelope(json)) {
                        const beErrors = extractFieldErrors(json);
                        const err = new Error(
                            json?.message ?? `Failed for KYC Type ${typeId}`
                        );
                        (err as Error & { beErrors?: FieldErrors }).beErrors = beErrors;
                        throw err;
                    }
                    return typeId;
                })
            );

            const failed = results.filter(r => r.status === 'rejected') as Array<
                PromiseRejectedResult
            >;
            const succeededCount = results.length - failed.length;

            // Refresh table whenever at least one row landed so the user sees committed state.
            if (succeededCount > 0) {
                dispatch(resetPspKycSettings());
            }

            if (failed.length === 0) {
                resetForm();
                onSuccess?.();
                return true;
            }

            // Surface the first failure's field errors so the UI highlights the right control.
            const firstErr = failed[0].reason as Error & { beErrors?: FieldErrors };
            if (firstErr?.beErrors && Object.keys(firstErr.beErrors).length > 0) {
                setFieldErrors(firstErr.beErrors);
            }

            setError(
                succeededCount === 0
                    ? (firstErr?.message ?? 'Failed to create PSP KYC settings')
                    : `${failed.length} of ${results.length} KYC settings failed to create.`
                        + ' The rest were saved.'
            );
            return false;
        } catch (err: any) {
            setError(err?.message ?? 'Failed to create PSP KYC setting');
            return false;
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        formValues,
        fieldErrors,
        error,
        isSubmitting,
        onFieldChange,
        validateForm,
        resetForm,
        handleSubmit
    };
}
